import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/auth-context';
import {
  updateUserProfile,
  fetchCurrentUserProfile,
  deleteCertificate,
  deleteDiploma,
  addCertificate,
  addDiploma,
  updateCertificate,
  updateDiploma,
  getKraPin,
  saveKraPin,
} from '../api/services';
import UserProfile from '../components/UserProfile';
import KraPin from '../components/KraPin';
import Skill from '../components/Skill';
import Language from '../components/Language';
import Certificate from '../components/Certificate';
import Diploma from '../components/Diploma';
import { Box, Snackbar, Alert } from '@mui/material';
import dayjs from 'dayjs';

// Only ADMIN gets view-only access to KRA Pin
const KRA_PIN_VIEW_ONLY_ROLES = ['ADMIN'];

// Offset so anchored sections land below the fixed navbar when scrolled to
const sectionAnchorSx = { scrollMarginTop: { xs: '100px', md: '120px' } };

const ProfileManager = ({ setProgress }) => {
  const { user } = useContext(AuthContext);

  const isViewOnly = KRA_PIN_VIEW_ONLY_ROLES.includes(user?.role);

  const [profileData, setProfileData] = useState({
    name: '',
    email: user?.email || '',
    jobTitle: '',
    department: '',
    employeeId: '',
    skills: [],
    languages: [],
    certificates: [],
    diplomas: [],
    kraPin: {
      pinNumber: '',
      fileName: '',
      fileUrl: '',
      file: null,
    },
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Calculate profile progress based on skills, languages, certificates, diplomas
  const calculateProfileProgress = (data) => {
    let progress = 0;

    // Check Skills section (25%)
    if (data.skills && data.skills.length > 0) {
      progress += 25;
    }

    // Check Languages section (25%)
    if (data.languages && data.languages.length > 0) {
      progress += 25;
    }

    // Check Certificates section (25%)
    if (data.certificates && data.certificates.length > 0) {
      progress += 25;
    }

    // Check Diplomas section (25%)
    if (data.diplomas && data.diplomas.length > 0) {
      progress += 25;
    }

    return progress;
  };

  // Update progress whenever profileData changes
  useEffect(() => {
    const newProgress = calculateProfileProgress(profileData);
    setProgress(newProgress);
  }, [profileData, setProgress]);

  // Loads user profile data on mount
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const currentUserProfile = await fetchCurrentUserProfile();
        if (currentUserProfile) {
          const updatedProfileData = {
            name: currentUserProfile.name || '',
            email: currentUserProfile.email || user?.email || '',
            jobTitle: currentUserProfile.jobTitle || '',
            department: currentUserProfile.department || '',
            employeeId: currentUserProfile.employeeId || '',
            skills: Array.isArray(currentUserProfile.skills) ? currentUserProfile.skills : [],
            languages: Array.isArray(currentUserProfile.languages) ? currentUserProfile.languages : [],
            certificates: Array.isArray(currentUserProfile.certificates)
              ? currentUserProfile.certificates.map((cert) => ({
                  id: cert.id || crypto.randomUUID(),
                  name: cert.name || '',
                  organization: cert.organization || '',
                  issueDate: cert.issueDate ? dayjs(cert.issueDate) : null,
                  expiryDate: cert.expiryDate ? dayjs(cert.expiryDate) : null,
                  url: cert.url || '',
                  comments: cert.comments || '',
                  mediaFilePath: cert.mediaFilePath || '',
                }))
              : [],
            diplomas: Array.isArray(currentUserProfile.diplomas)
              ? currentUserProfile.diplomas.map((diploma) => ({
                  id: diploma.id || crypto.randomUUID(),
                  name: diploma.name || '',
                  organization: diploma.organization || '',
                  issueDate: diploma.issueDate ? dayjs(diploma.issueDate) : null,
                  url: diploma.url || '',
                  comments: diploma.comments || '',
                  mediaFilePath: diploma.mediaFilePath || '',
                }))
              : [],
          };

          // Merge instead of replace — loadKraPin owns profileData.kraPin,
          // so we never overwrite it here regardless of which effect resolves first.
          setProfileData((prev) => ({ ...prev, ...updatedProfileData }));
        } else {
          setSnackbar({
            open: true,
            message: 'User profile not found.',
            severity: 'warning',
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: `Error fetching user profile: ${error.response?.data?.message || error.message}`,
          severity: 'error',
        });
      }
    };

    if (user?.email) {
      loadUserProfile();
    }
  }, [user?.email]);

  // Loads KRA Pin data on mount
  useEffect(() => {
    const loadKraPin = async () => {
      try {
        const data = await getKraPin();
        setProfileData((prev) => ({
          ...prev,
          kraPin: {
            pinNumber: data?.kraPin || '',
            fileName: data?.hasDocument ? (data?.documentName || '') : '',
            fileUrl: '',
            file: null,
          },
        }));
      } catch (error) {
        // 404 just means no KRA Pin set yet — not an error state worth surfacing
        if (error?.response?.status !== 404) {
          setSnackbar({
            open: true,
            message: `Error fetching KRA Pin: ${error.response?.data?.message || error.message}`,
            severity: 'error',
          });
        }
      }
    };

    if (user?.email) {
      loadKraPin();
    }
  }, [user?.email]);

  // Saves updated profile data to backend
  const saveProfile = async (updatedData) => {
    try {
      const response = await updateUserProfile(updatedData);
      let hasError = false;
      let errorMessages = [];

      if (updatedData.name && response.name !== updatedData.name) {
        errorMessages.push('Name could not be saved.');
        hasError = true;
      }
      if (updatedData.email && response.email !== updatedData.email) {
        errorMessages.push('Email could not be saved.');
        hasError = true;
      }
      if (updatedData.jobTitle && response.jobTitle !== updatedData.jobTitle) {
        errorMessages.push('Job Title could not be saved.');
        hasError = true;
      }
      if (updatedData.department && response.department !== updatedData.department) {
        errorMessages.push('Department could not be saved.');
        hasError = true;
      }
      if (updatedData.employeeId && response.employeeId !== updatedData.employeeId) {
        errorMessages.push('Employee ID could not be saved.');
        hasError = true;
      }
      if (updatedData.skills.length > 0 && (!response.skills || response.skills.length === 0)) {
        errorMessages.push('Skills could not be saved.');
        hasError = true;
      }
      if (updatedData.languages.length > 0 && (!response.languages || response.languages.length === 0)) {
        errorMessages.push('Languages could not be saved.');
        hasError = true;
      }

      const updatedProfileData = {
        name: response.name || '',
        email: response.email || user?.email || '',
        jobTitle: response.jobTitle || '',
        department: response.department || '',
        employeeId: response.employeeId || '',
        skills: Array.isArray(response.skills) ? response.skills : [],
        languages: Array.isArray(response.languages) ? response.languages : [],
        certificates: Array.isArray(response.certificates)
          ? response.certificates.map((cert) => ({
              id: cert.id || crypto.randomUUID(),
              name: cert.name || '',
              organization: cert.organization || '',
              issueDate: cert.issueDate ? dayjs(cert.issueDate) : null,
              expiryDate: cert.expiryDate ? dayjs(cert.expiryDate) : null,
              url: cert.url || '',
              comments: cert.comments || '',
              mediaFilePath: cert.mediaFilePath || '',
            }))
          : [],
        diplomas: Array.isArray(response.diplomas)
          ? response.diplomas.map((diploma) => ({
              id: diploma.id || crypto.randomUUID(),
              name: diploma.name || '',
              organization: diploma.organization || '',
              issueDate: diploma.issueDate ? dayjs(diploma.issueDate) : null,
              url: diploma.url || '',
              comments: diploma.comments || '',
              mediaFilePath: diploma.mediaFilePath || '',
            }))
          : [],
      };

      // Merge instead of replace — preserve kraPin, which this save flow doesn't touch.
      setProfileData((prev) => ({ ...prev, ...updatedProfileData }));

      setSnackbar({
        open: true,
        message: hasError ? `${errorMessages.join(' ')} Please try again or contact support.` : 'Profile updated successfully!',
        severity: hasError ? 'warning' : 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error updating profile: ${error.response?.data?.message || error.message}`,
        severity: 'error',
      });
    }
  };

  // Formats certificates or diplomas for backend
  const formatForBackend = (items, isCertificate = true) => {
    return Array.isArray(items)
      ? items.map((item) => {
          const formattedItem = {
            id: item.id || crypto.randomUUID(),
            name: item.name || '',
            organization: item.organization || '',
            issueDate: item.issueDate && dayjs(item.issueDate).isValid()
              ? dayjs(item.issueDate).format('YYYY-MM-DD')
              : '',
            url: item.url || '',
            comments: item.comments || '',
          };

          if (isCertificate) {
            formattedItem.expiryDate = item.expiryDate && dayjs(item.expiryDate).isValid()
              ? dayjs(item.expiryDate).format('YYYY-MM-DD')
              : '';
          }

          return formattedItem;
        })
      : [];
  };

  // Updates basic profile fields
  const handleProfileChange = (updatedFields) => {
    setProfileData((prev) => {
      const updated = { ...prev, ...updatedFields };
      const updatedProfileData = {
        name: updatedFields.name || updated.name || '',
        email: updatedFields.email || updated.email || user?.email || '',
        jobTitle: updatedFields.jobTitle || updated.jobTitle || '',
        department: updatedFields.department || updated.department || '',
        employeeId: updatedFields.employeeId || updated.employeeId || '',
        skills: Array.isArray(updated.skills) ? updated.skills : [],
        languages: Array.isArray(updated.languages) ? updated.languages : [],
        certificates: formatForBackend(updated.certificates, true),
        diplomas: formatForBackend(updated.diplomas, false),
      };
      saveProfile(updatedProfileData);
      return updated;
    });
  };

  // Updates skills
  const handleSkillsChange = (newSkills) => {
    const safeSkills = Array.isArray(newSkills) ? newSkills : [];
    setProfileData((prev) => {
      const updated = { ...prev, skills: safeSkills };
      const updatedProfileData = {
        name: updated.name || '',
        email: updated.email || user?.email || '',
        jobTitle: updated.jobTitle || '',
        department: updated.department || '',
        employeeId: updated.employeeId || '',
        skills: safeSkills,
        languages: Array.isArray(updated.languages) ? updated.languages : [],
        certificates: formatForBackend(updated.certificates, true),
        diplomas: formatForBackend(updated.diplomas, false),
      };
      saveProfile(updatedProfileData);
      return updated;
    });
  };

  // Updates languages
  const handleLanguagesChange = (newLanguages) => {
    const safeLanguages = Array.isArray(newLanguages) ? newLanguages : [];
    setProfileData((prev) => {
      const updated = { ...prev, languages: safeLanguages };
      const updatedProfileData = {
        name: updated.name || '',
        email: updated.email || user?.email || '',
        jobTitle: updated.jobTitle || '',
        department: updated.department || '',
        employeeId: updated.employeeId || '',
        skills: Array.isArray(updated.skills) ? updated.skills : [],
        languages: safeLanguages,
        certificates: formatForBackend(updated.certificates, true),
        diplomas: formatForBackend(updated.diplomas, false),
      };
      saveProfile(updatedProfileData);
      return updated;
    });
  };

  // Handles certificate additions and updates
  const handleCertificatesChange = async (update) => {
    try {
      const prevCertificates = profileData.certificates || [];
      let updatedCertificates = [...prevCertificates];

      if (typeof update === 'object' && update.action) {
        const { action, certificate, index } = update;
        const formattedCertificate = formatForBackend([certificate], true)[0];

        const requiredFields = ['id', 'name', 'organization', 'issueDate', 'url', 'comments'];
        const missingFields = requiredFields.filter(field => !formattedCertificate[field] || formattedCertificate[field].trim() === '');
        if (missingFields.length > 0) {
          setSnackbar({
            open: true,
            message: `Please fill in all required certificate fields: ${missingFields.join(', ')}`,
            severity: 'error',
          });
          return;
        }

        if (action === 'add') {
          const { id, ...certificateWithoutId } = formattedCertificate;
          const response = await addCertificate(certificateWithoutId);
          updatedCertificates = [...updatedCertificates, { ...certificateWithoutId, id: response.id || certificate.id }];
          setSnackbar({
            open: true,
            message: 'Certificate added successfully!',
            severity: 'success',
          });
        } else if (action === 'update') {
          await updateCertificate(certificate.id, formattedCertificate);
          updatedCertificates[index] = formattedCertificate;
          setSnackbar({
            open: true,
            message: 'Certificate updated successfully!',
            severity: 'success',
          });
        } else {
          throw new Error('Invalid action type');
        }
      } else {
        const safeCertificates = Array.isArray(update) ? update : [];
        for (const certificate of safeCertificates) {
          const formattedCertificate = formatForBackend([certificate], true)[0];
          const isExisting = prevCertificates.some(prevCert => prevCert.id === certificate.id);
          if (isExisting) {
            await updateCertificate(certificate.id, formattedCertificate);
            updatedCertificates = updatedCertificates.map(cert =>
              cert.id === certificate.id ? formattedCertificate : cert
            );
          } else {
            const { id, ...certificateWithoutId } = formattedCertificate;
            const response = await addCertificate(certificateWithoutId);
            updatedCertificates = [...updatedCertificates, { ...certificateWithoutId, id: response.id || certificate.id }];
          }
        }
      }

      setProfileData(prev => ({ ...prev, certificates: updatedCertificates }));
      const currentUserProfile = await fetchCurrentUserProfile();
      if (currentUserProfile) {
        setProfileData(prev => ({
          ...prev,
          certificates: Array.isArray(currentUserProfile.certificates)
            ? currentUserProfile.certificates.map((cert) => ({
                id: cert.id || crypto.randomUUID(),
                name: cert.name || '',
                organization: cert.organization || '',
                issueDate: cert.issueDate ? dayjs(cert.issueDate) : null,
                expiryDate: cert.expiryDate ? dayjs(cert.expiryDate) : null,
                url: cert.url || '',
                comments: cert.comments || '',
                mediaFilePath: cert.mediaFilePath || '',
              }))
            : [],
        }));
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error handling certificate: ${error.message}`,
        severity: 'error',
      });
    }
  };

  // Handles diploma additions and updates
  const handleDiplomasChange = async (update) => {
    try {
      const prevDiplomas = profileData.diplomas || [];
      let updatedDiplomas = [...prevDiplomas];

      if (typeof update === 'object' && update.action) {
        const { action, diploma, index } = update;
        const formattedDiploma = formatForBackend([diploma], false)[0];

        const requiredFields = ['id', 'name', 'organization', 'issueDate', 'url', 'comments'];
        const missingFields = requiredFields.filter(field => !formattedDiploma[field] || formattedDiploma[field].trim() === '');
        if (missingFields.length > 0) {
          setSnackbar({
            open: true,
            message: `Please fill in all required diploma fields: ${missingFields.join(', ')}`,
            severity: 'error',
          });
          return;
        }

        if (action === 'add') {
          const { id, ...diplomaWithoutId } = formattedDiploma;
          const response = await addDiploma(diplomaWithoutId);
          updatedDiplomas = [...updatedDiplomas, { ...diplomaWithoutId, id: response.id || diploma.id }];
          setSnackbar({
            open: true,
            message: 'Diploma added successfully!',
            severity: 'success',
          });
        } else if (action === 'update') {
          await updateDiploma(diploma.id, formattedDiploma);
          updatedDiplomas[index] = formattedDiploma;
          setSnackbar({
            open: true,
            message: 'Diploma updated successfully!',
            severity: 'success',
          });
        } else {
          throw new Error('Invalid action type');
        }
      } else {
        const safeDiplomas = Array.isArray(update) ? update : [];
        for (const diploma of safeDiplomas) {
          const formattedDiploma = formatForBackend([diploma], false)[0];
          const isExisting = prevDiplomas.some(prevDip => prevDip.id === diploma.id);
          if (isExisting) {
            await updateDiploma(diploma.id, formattedDiploma);
            updatedDiplomas = updatedDiplomas.map(dip =>
              dip.id === diploma.id ? formattedDiploma : dip
            );
          } else {
            const { id, ...diplomaWithoutId } = formattedDiploma;
            const response = await addDiploma(diplomaWithoutId);
            updatedDiplomas = [...updatedDiplomas, { ...diplomaWithoutId, id: response.id || diploma.id }];
          }
        }
      }

      setProfileData(prev => ({ ...prev, diplomas: updatedDiplomas }));
      const currentUserProfile = await fetchCurrentUserProfile();
      if (currentUserProfile) {
        setProfileData(prev => ({
          ...prev,
          diplomas: Array.isArray(currentUserProfile.diplomas)
            ? currentUserProfile.diplomas.map((diploma) => ({
                id: diploma.id || crypto.randomUUID(),
                name: diploma.name || '',
                organization: diploma.organization || '',
                issueDate: diploma.issueDate ? dayjs(diploma.issueDate) : null,
                url: diploma.url || '',
                comments: diploma.comments || '',
                mediaFilePath: diploma.mediaFilePath || '',
              }))
            : [],
        }));
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error handling diploma: ${error.message}`,
        severity: 'error',
      });
    }
  };

  // Deletes a certificate
  const handleDeleteCertificate = async (certificateId) => {
    try {
      await deleteCertificate(certificateId);
      const updatedCertificates = profileData.certificates.filter(cert => cert.id !== certificateId);
      setProfileData(prev => ({ ...prev, certificates: updatedCertificates }));
      setSnackbar({
        open: true,
        message: 'Certificate deleted successfully!',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error deleting certificate: ${error.message}`,
        severity: 'error',
      });
    }
  };

  // Deletes a diploma
  const handleDeleteDiploma = async (diplomaId) => {
    try {
      await deleteDiploma(diplomaId);
      const updatedDiplomas = profileData.diplomas.filter(dip => dip.id !== diplomaId);
      setProfileData(prev => ({ ...prev, diplomas: updatedDiplomas }));
      setSnackbar({
        open: true,
        message: 'Diploma deleted successfully!',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error deleting diploma: ${error.message}`,
        severity: 'error',
      });
    }
  };

  // Saves the KRA Pin to the backend
  const handleKraPinChange = async (draft) => {
    try {
      const response = await saveKraPin({ kraPin: draft.pinNumber, file: draft.file });
      setProfileData((prev) => ({
        ...prev,
        kraPin: {
          pinNumber: response?.kraPin || '',
          fileName: response?.hasDocument ? (response?.documentName || '') : '',
          fileUrl: '',
          file: null,
        },
      }));
      setSnackbar({
        open: true,
        message: 'KRA Pin saved successfully!',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error saving KRA Pin: ${error.response?.data?.message || error.message}`,
        severity: 'error',
      });
    }
  };

  // Closes snackbar
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box>
      <Box id="profile-section-user" sx={sectionAnchorSx}>
        <UserProfile
          name={profileData.name}
          email={profileData.email}
          jobTitle={profileData.jobTitle}
          department={profileData.department}
          employeeId={profileData.employeeId}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          handleProfileChange={handleProfileChange}
        />
      </Box>
      <KraPin
        kraPin={profileData.kraPin}
        onChange={handleKraPinChange}
        isViewOnly={isViewOnly}
      />
      <Skill
        selectedSkills={profileData.skills}
        setSelectedSkills={handleSkillsChange}
      />
      <Language
        selectedLanguages={profileData.languages}
        setSelectedLanguages={handleLanguagesChange}
      />
      <Box id="profile-section-certificates" sx={sectionAnchorSx}>
        <Certificate
          certificates={profileData.certificates}
          setCertificates={handleCertificatesChange}
          onDeleteCertificate={handleDeleteCertificate}
        />
      </Box>
      <Box id="profile-section-diplomas" sx={sectionAnchorSx}>
        <Diploma
          diplomas={profileData.diplomas}
          setDiplomas={handleDiplomasChange}
          onDeleteDiploma={handleDeleteDiploma}
        />
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfileManager;